import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCreateCallScriptMutation, useUpdateCallScriptMutation } from "@/store/rest_api";
import type { CallScript } from "@/model/interface/Call";
import { SCRIPT_TOKENS, tokenText } from "@/util/scriptTokens";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface CallScriptFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  script: CallScript | null;
}

export default function CallScriptFormDialog({ open, onOpenChange, script }: CallScriptFormDialogProps) {
  const [createScript, { isLoading: isCreating }] = useCreateCallScriptMutation();
  const [updateScript, { isLoading: isUpdating }] = useUpdateCallScriptMutation();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const isSaving = isCreating || isUpdating;

  const insertToken = (text: string) => {
    const textarea = bodyRef.current;
    const start = textarea?.selectionStart ?? body.length;
    const end = textarea?.selectionEnd ?? body.length;
    const nextBody = `${body.slice(0, start)}${text}${body.slice(end)}`;
    setBody(nextBody);
    window.requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const handleSave = async () => {
    try {
      if (script) {
        await updateScript({ id: script.id, data: { name, body } }).unwrap();
        toast.success("Script updated");
      } else {
        await createScript({ name, body }).unwrap();
        toast.success("Script created");
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the script"));
    }
  };

  useEffect(() => {
    if (open) {
      setName(script?.name ?? "");
      setBody(script?.body ?? "");
    }
  }, [open, script]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{script ? "Edit Script" : "New Script"}</DialogTitle>
          <DialogDescription>
            Fields fill in from the lead, their property and you when the call connects.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="call-script-name">Name</Label>
            <Input
              id="call-script-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Cash offer opener"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="call-script-body">Script</Label>
            <div className="flex flex-wrap gap-1.5">
              {SCRIPT_TOKENS.map((token) => (
                <Button
                  key={token.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => insertToken(tokenText(token))}
                >
                  {token.label}
                </Button>
              ))}
            </div>
            <Textarea
              id="call-script-body"
              ref={bodyRef}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={12}
              placeholder="Hi {{firstName}}, this is {{agentName}} with {{businessName}}. I'm calling about {{propertyAddress}}."
              className="font-mono text-sm"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving || !name.trim() || !body.trim()}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {script ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
